import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'
import * as Yup from 'yup'
import { AnyObject } from 'yup/lib/types'
import { useMutation } from '@tanstack/react-query'
import { ObjectShape, TypeOfShape } from 'yup/lib/object'
import { FormikHelpers } from 'formik'
import { useToast } from '@machobear/design-system'
import { Userpilot } from 'userpilot'
import {
  usePaymentValidation,
  initialValues as checkoutInitialValues,
  PaymentFormValuesType
} from '@/hooks/use-payment-validation'
import { add } from '@/models/plans/apis'
import { CatalogProduct } from '@/features/Payments/types'
import { Wizard, WizardStep } from '@/components/modal-wizard-form'
import queryClient from '@/components/query-client'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { useWizardContext } from '@/components/modal-wizard-form/wizard-context'
import { usePaymentContext } from '@/components/ds-checkout-modal/payment-context'
import { AddingPaymentMethod } from '@/components/ds-checkout-modal/types'

import { useSponsoredListingCreditInformation } from '@/hooks/use-block-and-pay'
import usePaymentMethod from '@/hooks/use-payments'
import {
  useGetProduct,
  postNewSponsoredListing,
  useGetSponsoredListing,
  useSponsorListing
} from '@/hooks/use-sponsored-listing'
import CheckoutStep from '@/components/checkout-step'
import {
  SponsoredListingForm,
  SponsorListingFormType,
  NewSponsoredListingResponseType,
  ApiResponse
} from '../sponsored-listing-form'

interface CustomWindow extends Window {
  dataLayer: Record<string, unknown>[]
}

declare let window: CustomWindow

type FormValuesType = SponsorListingFormType & PaymentFormValuesType

const handleTimeValidation = (
  time: TypeOfShape<ObjectShape>,
  formSchema: Yup.TestContext<AnyObject>
) => {
  const startDate = formSchema.parent.planned_start_date
  const timezone = formSchema.parent.timezone
  const currentTime = moment(new Date())

  const selectedTime = moment().tz(timezone).set({
    date: startDate.getDate(),
    month: startDate.getMonth(),
    year: startDate.getFullYear(),
    hour: time?.hour,
    minute: time?.minute,
    second: 0
  })

  const isInFuture = selectedTime.isAfter(currentTime)

  if (!isInFuture) {
    pushToDatalayer({
      event: 'app_sponsor_err_alert'
    })
  }
  return isInFuture
}

const formatProduct = (product?: CatalogProduct) => {
  return {
    items: [
      {
        priceId: product?.price_id,
        quantity: 1,
        useCredits: false
      }
    ],
    metadata: {
      plan_name: product?.plan_name
    }
  }
}

const getScheduleValues = (formValues: SponsorListingFormType) => {
  let plannedStartValues = {}
  if (formValues?.immediate_start === 'false') {
    plannedStartValues = {
      planned_start_date: moment(formValues?.planned_start_date).format('YYYY-MM-DD'),
      planned_start_time: moment()
        .set({
          hour: formValues?.planned_start_time?.hour,
          minute: formValues?.planned_start_time?.minute,
          second: 0
        })
        .format('HH:mm:ss'),
      timezone: formValues?.timezone,
      planned_end_date: moment(formValues?.planned_end_date).format('YYYY-MM-DD')
    }
  }

  return plannedStartValues
}

const getDuration = (value: string | null) => {
  switch (value) {
    case 'seven-days-sponsored-listing-package':
      return '7'
    case 'fourteen-days-sponsored-listing-package':
      return '14'
    case 'thirty-days-sponsored-listing-package':
      return '28'
    default:
      return null
  }
}

const getTrackingId = (value: string | null, isCreateListing: boolean) => {
  let result = null

  if (value === 'seven-days-sponsored-listing-package') {
    result = 'goal_purchase_sl_7d'
  } else if (value === 'fourteen-days-sponsored-listing-package') {
    result = 'goal_purchase_sl_14d'
  } else if (value === 'thirty-days-sponsored-listing-package') {
    result = 'goal_purchase_sl_28d'
  }

  return result && isCreateListing ? `${result}_create` : result
}

const SponsorWizardFormContent = ({
  listingId,
  onSuccess,
  onClose,
  initialFormValues,
  initialStep = 0,
  isCreateListing = false
}: {
  listingId: string
  onSuccess: () => void
  onClose: () => void
  initialFormValues: Partial<SponsorListingFormType>
  initialStep?: number
  isCreateListing?: boolean
}) => {
  const { t } = useTranslation()
  const showToast = useToast()
  const { paymentInfo = [] } = usePaymentMethod()
  const { setIsSubmitting, setSubmittingFinished, setIsLoading, setLoadingFinished } =
    useWizardContext()
  const paymentSchema = usePaymentValidation()
  const checkoutValidationSchema = paymentInfo?.length === 0 ? paymentSchema : null
  const [productPackage, setProductPackage] = useState<string | null>(null)
  const { availableCredits } = useSponsoredListingCreditInformation()
  const { refetch: refetchSponsoredListings } = useGetSponsoredListing(listingId)
  const { purchaseSponsoredListing } = useSponsorListing(listingId)
  const { setProduct, coupon, product, setCouponApplyButtonId, setCouponApplyCallback } =
    usePaymentContext()

  const selectedCount = getDuration(productPackage)
  const hasEnoughCredits = Number(availableCredits) >= Number(selectedCount)

  const handleCouponeApplyEventIds = useCallback(() => {
    pushToDatalayer({
      event: 'application-sponsored-listings-coupon-apply-btn-submit'
    })
  }, [])

  // Get product info

  // Currently calling hook 3 times each with different filters because BE only allows for 1 filter
  // I believe this will be updated in the future so we can reduce this to one call
  const { data: sevenDaysPackage, isLoading: isSevenDaysPackageLoading } = useGetProduct(
    'seven-days-sponsored-listing-package'
  )
  const { data: fourteenDaysPackage, isLoading: isFourteenDaysPackageLoading } = useGetProduct(
    'fourteen-days-sponsored-listing-package'
  )
  const { data: thirtyDaysPackage, isLoading: isThirtyDaysPackageLoading } = useGetProduct(
    'thirty-days-sponsored-listing-package'
  )

  const isProductLoading =
    isSevenDaysPackageLoading || isFourteenDaysPackageLoading || isThirtyDaysPackageLoading

  const products = [
    sevenDaysPackage?.products[0],
    fourteenDaysPackage?.products[0],
    thirtyDaysPackage?.products[0]
  ]

  const getProduct = (p: string | null): CatalogProduct | undefined => {
    return products?.filter((product: CatalogProduct | undefined) => product?.plan_name === p)[0]
  }

  const initialValues = {
    immediate_start: 'true',
    duration: undefined,
    planned_start_date: new Date(),
    planned_start_time: { hour: 9, minute: 0 },
    planned_end_date: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...checkoutInitialValues,
    ...initialFormValues
  }

  const handleSponsorSuccess = () => {
    onSuccess()
    onClose()
    refetchSponsoredListings()
  }

  const handleContinue = (values: SponsorListingFormType) => {
    if (!hasEnoughCredits) {
      pushToDatalayer({
        event: 'application-sponsor-listing-modal-continue'
      })
    }

    pushToDatalayer({
      event:
        values?.immediate_start === 'true'
          ? 'application-sponsored-listings-start-date-immediate-submit'
          : 'application-sponsored-listings-start-date-schedule-later-submit'
    })
  }

  const getSuccessToasterText = (body?: NewSponsoredListingResponseType) => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startTime = moment(body?.planned_start_date).tz(localTimezone).format('hh:mm A')
    const startDate = moment(body?.planned_start_date).tz(localTimezone).format('YYYY-MM-DD')

    const isToday = moment(body?.planned_start_date).tz(localTimezone).isSame(new Date(), 'day')
    const isTomorrow = moment(body?.planned_start_date)
      .tz(localTimezone)
      .subtract(1, 'day')
      .isSame(new Date(), 'day')

    if (isToday)
      return t(
        'l.listing.sponsored_listings.success_today.toaster',
        `Success! This listing will be sponsored today at {{ startTime }}`,
        { startTime }
      )
    else if (isTomorrow) {
      return t(
        'l.listing.sponsored_listings.success_tomorrow.toaster',
        `Success! This listing will be sponsored tomorrow at {{ startTime }}`,
        { startTime }
      )
    } else {
      return t(
        'l.listing.sponsored_listings.success_future.toaster',
        `Success! This listing will be sponsored on {{ startDate }} at {{ startTime }}`,
        { startTime, startDate }
      )
    }
  }

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values)
  })

  const handleEventIds = (isPurchase?: boolean) => {
    const product = getProduct(productPackage)
    const planName = product?.plan_name
    const price = product?.total_before_tax
    const userPilotTrackingId = getTrackingId(productPackage, isCreateListing)
    if (isPurchase) {
      pushToDatalayer({
        event: isCreateListing
          ? 'create-application-sponsor-listing-checkout-submit-purchase-success'
          : 'application-sponsor-listing-checkout-submit-purchase-success',
        purchaseId: planName
      })
      if (userPilotTrackingId) Userpilot.track(userPilotTrackingId, { price })
    } else {
      pushToDatalayer({
        event: 'application-sponsor-listing-credit-checkout-submit-purchase-success',
        purchaseId: planName
      })
      pushToDatalayer({
        event: 'app_sponsor_cr_purchased'
      })
      localStorage.removeItem('promoType')
    }
  }

  const handleSponsorListing = async (values: FormValuesType, isPurchase?: boolean) => {
    const plannedStartValues = getScheduleValues(values)
    const result: ApiResponse<NewSponsoredListingResponseType> = await postNewSponsoredListing({
      body: {
        listing_id: parseInt(listingId),
        plan_name: getProduct(productPackage)?.plan_name,
        product_name: 'sponsored-listing-product',
        immediate_start: values?.immediate_start === 'true',
        ...plannedStartValues
      },
      listingId: listingId
    })
    setSubmittingFinished()
    if (result?.response.ok) {
      queryClient.invalidateQueries({ queryKey: ['blocks'] })
      handleSponsorSuccess()
      handleEventIds(isPurchase)
      showToast({
        message: getSuccessToasterText(result?.body),
        tone: 'positive'
      })
    }
  }

  const createPaymentMethodPurchaseAndSponsor = async (
    values: FormValuesType,
    actions: FormikHelpers<FormValuesType>
  ) => {
    addMethod.mutate(
      {
        payment_method: values.payment_method,
        save_payment_method: values.save_payment_method,
        default_payment_method: values.default_payment_method,
        street1: values?.street1,
        street2: values?.street2,
        card_number: values.card_number,
        expiration_date: values.expiration_date.replace('/', ''),
        first_name: values?.first_name,
        last_name: values?.last_name,
        city: values?.city,
        country: values?.country,
        zip: values?.zip,
        state: values?.state,
        security_code: values?.security_code
      },
      {
        onSuccess: async data => {
          await purchaseSponsoredListing.mutateAsync({
            planName: getProduct(productPackage)?.plan_name || '',
            paymentMethodId: data[0].payment_method_id,
            discountCode: coupon,

            onSuccess: async () => {
              await handleSponsorListing(values, true)
            }
          })
        }
      }
    )
  }

  const handlePurchaseAndSponsor = async (
    values: FormValuesType,
    actions: FormikHelpers<FormValuesType>
  ) => {
    const paymentMethodId = paymentInfo[0]?.payment_method_id
    if (paymentMethodId) {
      await purchaseSponsoredListing.mutateAsync({
        planName: getProduct(productPackage)?.plan_name || '',
        paymentMethodId: paymentMethodId,
        discountCode: coupon,

        onSuccess: async () => {
          await handleSponsorListing(values, true)
        }
      })
    } else {
      await createPaymentMethodPurchaseAndSponsor(values, actions)
    }
  }

  const handleSponsorListingSubmit = async (
    values: FormValuesType,
    actions: FormikHelpers<FormValuesType>
  ) => {
    setIsSubmitting()
    if (hasEnoughCredits) {
      await handleSponsorListing(values, false)
    } else {
      await handlePurchaseAndSponsor(values, actions)
    }
  }

  const handleDurationProductChange = useCallback(
    (productPackage: string | null) => {
      switch (productPackage) {
        case 'seven-days-sponsored-listing-package':
          setProduct(formatProduct(sevenDaysPackage?.products[0]))
          break
        case 'fourteen-days-sponsored-listing-package':
          setProduct(formatProduct(fourteenDaysPackage?.products[0]))
          break
        case 'thirty-days-sponsored-listing-package':
          setProduct(formatProduct(thirtyDaysPackage?.products[0]))
          break
      }
    },
    [
      fourteenDaysPackage?.products,
      setProduct,
      sevenDaysPackage?.products,
      thirtyDaysPackage?.products
    ]
  )

  const validationSchema = Yup.object().shape({
    immediate_start: Yup.string().oneOf(['true', 'false']).required(),
    planned_start_date: Yup.date().when('immediate_start', {
      is: 'true',
      then: Yup.date(),
      otherwise: Yup.date().required(
        t('l.listing.sponsored_listing.date.required', 'Please select a start date')
      )
    }),
    planned_start_time: Yup.object().when('immediate_start', {
      is: 'true',
      then: Yup.object(),
      otherwise: Yup.object()
        .required(t('l.listing.sponsored_listing.time.required', 'Please select a start time'))
        .test(
          'min_start_time',
          t(
            'l.listing.sponsored_listing.planned_start_time.passed',
            'Selected time has already passed'
          ),
          (time, formSchema) => handleTimeValidation(time, formSchema)
        )
    }),
    duration: Yup.string().required(t('common.required', 'Required'))
  })

  // Handles the use case when we need to send form values from the listing form to the checkout step
  useEffect(() => {
    if (!isProductLoading && initialFormValues?.duration) {
      setProductPackage(initialFormValues?.duration)
      handleDurationProductChange(initialFormValues?.duration)
    }
  }, [handleDurationProductChange, initialFormValues?.duration, isProductLoading])

  // Disables the Continue/Submit button if the product is still loading
  useEffect(() => {
    if (isProductLoading) {
      setIsLoading()
    } else {
      setLoadingFinished()
    }
  }, [isProductLoading, setIsLoading, setLoadingFinished])

  // Sets the button and event id for the coupon field
  useEffect(() => {
    setCouponApplyCallback(() => handleCouponeApplyEventIds)
    setCouponApplyButtonId('application-sponsored-listings-coupon-apply-btn-click')
  }, [handleCouponeApplyEventIds, setCouponApplyButtonId, setCouponApplyCallback])

  // Enabled
  const isPaymentFormEnabled = !selectedCount || !hasEnoughCredits

  return (
    <Wizard<FormValuesType>
      initialValues={initialValues}
      onSubmit={handleSponsorListingSubmit}
      intitalStep={initialStep}
    >
      <WizardStep
        validationSchema={validationSchema}
        enabled
        onSubmit={handleContinue}
        submitButtonId="application-sponsor-listing-modal-continue"
      >
        <SponsoredListingForm
          listingId={listingId}
          productPackage={productPackage}
          setProductPackage={setProductPackage}
          availableCredits={availableCredits}
          handleDurationProductChange={handleDurationProductChange}
        />
      </WizardStep>
      <WizardStep
        validationSchema={checkoutValidationSchema}
        enabled={isPaymentFormEnabled}
        submitButtonId="application-sponsor-listing-checkout-submit-purchase-click"
      >
        <CheckoutStep isLoadingProduct={!product?.items || isProductLoading} />
      </WizardStep>
    </Wizard>
  )
}

export default SponsorWizardFormContent
